import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bot, Check, X, ArrowRight } from 'lucide-react';
import type { NewTokenResponse, Token, TrialStatus } from '@grove/api-client';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ConnectAgentDialog } from '../components/ConnectAgentDialog';
import { useAuth } from '../contexts/AuthContext';
import { api, apiFetch } from '../lib/api';
import { MCP_DOCS_URL, MCP_TOOLS } from '../lib/mcp';

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function SettingsPage() {
  const { user, signOut } = useAuth();
  const queryClient = useQueryClient();
  const [newTokenLabel, setNewTokenLabel] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConnectAgent, setShowConnectAgent] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: tokens = [] } = useQuery<Token[]>({
    queryKey: ['tokens'],
    queryFn: () => api.listTokens()
  });

  const { data: trial } = useQuery<TrialStatus>({
    queryKey: ['trial-status'],
    queryFn: () => apiFetch('/user/quota')
  });

  const [billingError, setBillingError] = useState<string | null>(null);
  const openBillingPortal = useMutation({
    mutationFn: () => apiFetch<{ url: string }>('/stripe/portal', {
      method: 'POST',
      body: JSON.stringify({ returnUrl: window.location.href })
    }),
    onSuccess: (data) => { window.location.href = data.url; },
    onError: (err: any) => setBillingError(err.message ?? 'Could not open billing portal')
  });

  const startCheckout = useMutation({
    mutationFn: () => apiFetch<{ url: string }>('/stripe/create-checkout', { method: 'POST' }),
    onSuccess: (data) => { window.location.href = data.url; },
    onError: (err: any) => setBillingError(err.message ?? 'Could not start checkout')
  });

  const createToken = useMutation<NewTokenResponse>({
    mutationFn: () => api.createToken({ label: newTokenLabel }),
    onSuccess: (data) => {
      setCreatedToken(data.token);
      setNewTokenLabel('');
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    }
  });

  const revokeToken = useMutation({
    mutationFn: (id: string) => api.revokeToken(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tokens'] })
  });

  function handleCopy() {
    if (createdToken) {
      navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-text-primary mb-8">Settings</h1>

        {/* Account */}
        <Card as="section" padding="2xl" elevation={1} className="mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-4">Account</h2>
          <div className="space-y-3 mb-6">
            <div>
              <label className="block text-xs mb-1 text-text-muted">Name</label>
              <p className="text-sm text-text-primary">{user?.name ?? '—'}</p>
            </div>
            <div>
              <label className="block text-xs mb-1 text-text-muted">Email</label>
              <p className="text-sm text-text-primary">{user?.email}</p>
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => signOut()}>Sign out</Button>
        </Card>

        {/* Billing */}
        <Card as="section" padding="2xl" elevation={1} className="mb-6">
          <h2 className="text-base font-semibold text-text-primary mb-4">Billing</h2>
          <div className="mb-4">
            {trial?.status === 'active' && (
              <p className="text-sm text-text-primary">You're on Grove Pro.</p>
            )}
            {trial?.status === 'trialing' && (
              <p className="text-sm text-text-secondary">
                {trial.daysRemaining === 0 ? 'Your trial ends today.' : `${trial.daysRemaining} day${trial.daysRemaining === 1 ? '' : 's'} left in your trial.`}
              </p>
            )}
            {trial?.status === 'lapsed' && (
              <p className="text-sm text-text-secondary">
                Your trial has ended. Your library is still yours — subscribe to keep saving and syncing.
              </p>
            )}
          </div>
          {trial?.status === 'active' ? (
            <Button variant="secondary" size="sm" disabled={openBillingPortal.isPending}
              onClick={() => { setBillingError(null); openBillingPortal.mutate(); }}>
              {openBillingPortal.isPending ? 'Opening…' : 'Manage billing'}
            </Button>
          ) : (
            <Button size="sm" disabled={startCheckout.isPending}
              onClick={() => { setBillingError(null); startCheckout.mutate(); }}>
              {startCheckout.isPending ? 'Starting…' : 'Subscribe — $29/yr'}
            </Button>
          )}
          {billingError && <p className="text-sm text-red-400 mt-3">{billingError}</p>}
        </Card>

        {/* AI agents */}
        <Card as="section" padding="2xl" elevation={1} className="mb-6">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <h2 className="text-base font-semibold text-text-primary inline-flex items-center gap-2">
                <Bot size={16} className="text-prism-violet" /> AI agents
              </h2>
              <p className="text-xs mt-1 text-text-secondary">
                Grove ships an MCP server, so Claude, Cursor, or any MCP-compatible agent can work
                your queue directly.
              </p>
            </div>
            <Button size="sm" className="flex-shrink-0" onClick={() => setShowConnectAgent(true)}>
              + Connect an agent
            </Button>
          </div>

          <ul className="mt-4 space-y-1.5">
            {MCP_TOOLS.map(tool => (
              <li key={tool.name} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                <code className="font-mono text-prism-violet">{tool.name}</code>
                <span className="text-text-secondary">{tool.description}</span>
              </li>
            ))}
          </ul>

          <a
            href={MCP_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-violet-400 hover:text-violet-300 transition-colors"
          >
            MCP setup guide <ArrowRight size={14} />
          </a>
        </Card>

        {/* Personal Access Tokens */}
        <Card as="section" padding="2xl" elevation={1} className="mb-6">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <h2 className="text-base font-semibold text-text-primary">Personal Access Tokens</h2>
              <p className="text-xs mt-1 text-text-secondary">
                One credential for everything outside the app — the Obsidian plugin, agents you've
                connected, scripts hitting the Grove API.
              </p>
            </div>
            <Button size="sm" className="flex-shrink-0" onClick={() => setShowCreateModal(true)}>+ Create token</Button>
          </div>

          {tokens.length === 0 ? (
            <p className="text-sm mt-4 text-center py-6 text-text-muted">No tokens yet.</p>
          ) : (
            <div className="mt-4 rounded-lg border border-border-subtle divide-y divide-border-subtle bg-surface-0">
              <div className="grid grid-cols-3 px-4 py-2 text-xs font-medium text-text-muted">
                <span>Label</span><span>Created</span><span>Last used</span>
              </div>
              {tokens.map(t => (
                <div key={t.id} className="grid grid-cols-3 items-center px-4 py-3">
                  <span className="text-sm text-text-primary">{t.label}</span>
                  <span className="text-xs text-text-muted">{timeAgo(t.createdAt)}</span>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-muted">
                      {t.lastUsedAt ? timeAgo(t.lastUsedAt) : 'Never'}
                    </span>
                    <button onClick={() => revokeToken.mutate(t.id)}
                      className="text-red-400 hover:text-red-300 text-xs transition-colors inline-flex items-center" aria-label="Revoke token"><X size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Reader Preferences */}
        <Card as="section" padding="2xl" elevation={1}>
          <h2 className="text-base font-semibold text-text-primary mb-4">Reader Preferences</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm mb-2 text-text-secondary">Font size</label>
              <div className="flex gap-2">
                {['S', 'M', 'L', 'XL'].map(s => (
                  <button key={s}
                    className="px-3 py-1.5 rounded-lg text-sm border border-border-subtle hover:border-prism-violet/50 text-text-primary transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Set up Grove everywhere */}
        <Card as="section" padding="2xl" elevation={1} className="mt-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-text-primary">Set up Grove everywhere</h2>
              <p className="text-xs mt-1 text-text-secondary">
                Get the mobile app, browser extension, and Obsidian plugin.
              </p>
            </div>
            <Link
              to="/welcome"
              className="flex-shrink-0 inline-flex items-center gap-1 text-sm font-medium text-violet-400 hover:text-violet-300 transition-colors"
            >
              Open <ArrowRight size={14} />
            </Link>
          </div>
        </Card>
      </div>

      <ConnectAgentDialog open={showConnectAgent} onClose={() => setShowConnectAgent(false)} />

      {/* Create token modal */}
      <Modal open={showCreateModal && !createdToken} onClose={() => setShowCreateModal(false)} title="Create token">
        <div className="space-y-4">
          <div>
            <label className="block text-sm mb-1.5 text-text-primary">Label</label>
            <input type="text" value={newTokenLabel} onChange={e => setNewTokenLabel(e.target.value)}
              placeholder="e.g. Obsidian Plugin"
              className="w-full px-3 py-2 rounded-lg text-sm text-text-primary bg-surface-0 border border-border-subtle focus:border-prism-violet focus:outline-none" />
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button className="flex-1" disabled={!newTokenLabel || createToken.isPending}
              onClick={() => createToken.mutate()}>
              {createToken.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Show created token */}
      <Modal open={!!createdToken} onClose={() => { setCreatedToken(null); setShowCreateModal(false); }} title="Your new token">
        <div className="space-y-4">
          <div className="rounded-lg p-3 border border-prism-amber/30 bg-prism-amber/10">
            <p className="text-xs mb-2 inline-flex items-center gap-1.5 text-prism-amber">
              <AlertTriangle size={14} /> Copy this now — it won't be shown again.
            </p>
            <code className="text-xs text-text-primary break-all">{createdToken}</code>
          </div>
          <Button className="w-full" onClick={handleCopy}>
            <span className="inline-flex items-center gap-1.5">
              {copied ? (<><Check size={14} /> Copied!</>) : 'Copy to clipboard'}
            </span>
          </Button>
        </div>
      </Modal>
    </Layout>
  );
}
